import { FC, useEffect, useState } from 'react'

import { AccountType, InstrumentWithCampaignMetadataT, OrderAction } from '@/common/types'
import { Dialog, DialogContent } from '@/common/ui/dialog'
import { TradeForm } from './tradeForm'
import { TradeReview } from './tradeReview'
import useTradeStore from '@/trading/tradeStore'
import { OrderConfirmation } from './orderConfirmation'
import { TradeSelect } from './tradeSelect'
import { Spinner } from '../common/ui/spinner'
import { cn } from '@/common/utils/utils'
import { useFetchKYC } from '@/kyc/kycQueries'
import { useFetchTradingAccount } from './tradingQueries'
import { analytics } from '@/common/analytics/analytics'
import { NoTradingAccountAlert } from '@/trading/noTradingAccountAlert.tsx'
import useUserStore from '@/user/userStore'
import useAccountStore from '@/account/accountStore'
import { useCurrentAccount } from '@/account/useCurrentAccount'
import { NoKycWarning } from '@/kyc/noKycWarning'
import { ANALYTICS_EVENTS } from '@/common/analytics/events'
import { useOrderSource } from '@/common/context/useOrderSource'

type Props = {
    instruments: InstrumentWithCampaignMetadataT[] | null
    loading?: boolean
    onClose: () => void
    pledges?: { [key: string]: number } | null
    orderAction: OrderAction | null
}
export type TradeState = 'select' | 'order' | 'review' | 'success' | null

export const TradeModal: FC<Props> = ({ instruments, onClose, pledges, orderAction, loading = false }) => {
    const { orderSource } = useOrderSource()
    const [tradeStatus, setTradeStatus] = useState<TradeState>(null)
    const [instrument, setInstrument] = useState<InstrumentWithCampaignMetadataT | null>(null)
    const setRequestId = useTradeStore((state) => state.setRequestId)
    const resetState = useTradeStore((state) => state.resetState)
    const { setOrderAction } = useTradeStore()
    const [openKyc, setOpenKyc] = useState<boolean>(false)
    const closeKYCModal = () => setOpenKyc(false)

    const { user } = useUserStore()
    const account = useCurrentAccount()
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const { data: kyc } = useFetchKYC(activeAccountId, { enabled: true })
    const isKYCPending = kyc?.status && kyc?.status !== 'VERIFICATION_SUCCESS'
    const { data: tradingAccount } = useFetchTradingAccount(activeAccountId)
    const hasApprovedTradingAccount = tradingAccount?.status === 'APPROVED'
    const hasHIN = tradingAccount?.hin !== undefined

    const onSelectionMade = (instrument: InstrumentWithCampaignMetadataT) => {
        setInstrument(instrument)
        setTradeStatus('order')
        analytics.track('BuyOrderStock Selected', { security_code: instrument.securityCode })
    }

    const onBackToOrder = () => {
        setTradeStatus('order')
    }

    const onOrderReview = () => {
        setTradeStatus('review')
        analytics.track(ANALYTICS_EVENTS.TRADING.BUY_ORDER_REVIEWED, {
            security_code: instrument?.securityCode,
            order_from: orderSource.orderFrom,
            order_entry_point: orderSource.orderEntryPoint,
        })
    }

    const onOrderSubmit = () => {
        setTradeStatus('success')
    }

    const onModalClose = () => {
        setTradeStatus(null)
        resetState()
        onClose()
    }

    useEffect(() => {
        setRequestId()
    }, [setRequestId])

    useEffect(() => {
        if (!instruments || !orderAction) return
        if (instruments.length === 1) {
            setInstrument(instruments[0])
            setOrderAction(orderAction)
            setTradeStatus('order')
        } else {
            setTradeStatus('select')
        }
    }, [instruments, orderAction, setOrderAction])

    const tradeUI = (tradeStatus: TradeState) => {
        if (tradeStatus === 'select' && instruments) {
            return <TradeSelect instruments={instruments} onSelectionMade={onSelectionMade} pledges={pledges} />
        }

        if (!instrument) return null
        switch (tradeStatus) {
            case 'review':
                return (
                    <TradeReview
                        instrument={instrument}
                        hasApprovedTradingAccount={hasApprovedTradingAccount}
                        onBackToOrder={onBackToOrder}
                        onSubmit={onOrderSubmit}
                        pledges={pledges}
                    />
                )
            case 'success':
                return <OrderConfirmation instrument={instrument} onClose={onModalClose} />
            case 'order':
                return (
                    <TradeForm
                        instrument={instrument}
                        hasHIN={hasHIN}
                        onSubmit={onOrderReview}
                        onClose={onModalClose}
                    />
                )
            default:
                return null
        }
    }

    const loader = (
        <div className={cn('flex items-center justify-center')}>
            <Spinner size="lg" />
        </div>
    )

    return (
        <>
            <Dialog open={loading || (!!instruments && instruments.length > 0)}>
                <DialogContent
                    onClose={onModalClose}
                    className="max-h-[90vh] min-h-[50vh] max-w-[680px] overflow-y-auto py-14 px-10"
                >
                    {loading ? (
                        loader
                    ) : isKYCPending && !openKyc ? (
                        <NoKycWarning onKycClick={closeKYCModal} kycProviderEntityId={kyc.kycProviderEntityId} />
                    ) : !hasApprovedTradingAccount ? (
                        <NoTradingAccountAlert
                            swiftIdOnboarding={
                                (account?.accountType ?? user?.accountType) &&
                                [AccountType.COMPANY_TRUST, AccountType.INDIVIDUAL_TRUST, AccountType.COMPANY].includes(
                                    account?.accountType ?? user?.accountType ?? AccountType.INDIVIDUAL,
                                )
                            }
                        />
                    ) : (
                        tradeStatus && tradeUI(tradeStatus)
                    )}
                </DialogContent>
            </Dialog>
        </>
    )
}
